// Pure fretboard geometry: fret wire positions (12-TET scale-length rule,
// optionally compressed toward even spacing so high frets stay readable),
// string positions, note placement and pointer hit-testing. All x values are
// computed right-handed; `screenX`/`boardX` mirror for left-handed players.

/** Frets with position markers; 12 and 24 get double dots. */
export const INLAY_FRETS: readonly number[] = [3, 5, 7, 9, 12, 15, 17, 19, 21, 24];
export const DOUBLE_INLAYS: readonly number[] = [12, 24];

/**
 * Normalized positions (0 = nut, 1 = last fret) of fret wires 0…frets.
 * `compress` 0 = true 12-TET (d(n) = 1 − 2^(−n/12)), 1 = even spacing.
 */
export function fretPositions(frets: number, compress = 0.45): number[] {
  const n = Math.max(1, Math.round(frets));
  const c = Math.max(0, Math.min(1, compress));
  const full = 1 - Math.pow(2, -n / 12);
  const out: number[] = [];
  for (let i = 0; i <= n; i++) {
    const tet = (1 - Math.pow(2, -i / 12)) / full;
    out.push((1 - c) * tet + c * (i / n));
  }
  return out;
}

export interface FretboardGeometryInput {
  /** CSS px. */
  width: number;
  height: number;
  strings: number;
  frets: number;
  leftHanded?: boolean;
  compress?: number;
}

export interface FretboardGeometry {
  width: number;
  height: number;
  strings: number;
  frets: number;
  leftHanded: boolean;
  /** Right-handed x of fret wire i (0 = nut). */
  wireX: number[];
  nutX: number;
  /** Right-handed x of the open-string markers (left of the nut). */
  openX: number;
  /** Right-handed x of the tuning labels. */
  tuningX: number;
  boardTop: number;
  boardBottom: number;
  /** y of string s at index s − 1 (string 1 = top). */
  stringY: number[];
  spacing: number;
  /** Baseline for fret numbers. */
  fretNumberY: number;
  /** Baseline of the instrument label. */
  headerY: number;
}

export const FRETBOARD_PAD = { top: 18, bottom: 16, tuning: 20, open: 20, right: 10 } as const;

export function fretboardGeometry(inp: FretboardGeometryInput): FretboardGeometry {
  const strings = Math.max(1, Math.round(inp.strings));
  const frets = Math.max(1, Math.round(inp.frets));
  const P = FRETBOARD_PAD;
  const nutX = P.tuning + P.open;
  const right = Math.max(nutX + frets * 4, inp.width - P.right);
  const pos = fretPositions(frets, inp.compress);
  const wireX = pos.map((p) => nutX + p * (right - nutX));
  const boardTop = P.top;
  const boardBottom = Math.max(boardTop + strings * 6, inp.height - P.bottom);
  const spacing = (boardBottom - boardTop) / strings;
  const stringY = Array.from({ length: strings }, (_, i) => boardTop + spacing * (i + 0.5));
  return {
    width: inp.width,
    height: inp.height,
    strings,
    frets,
    leftHanded: !!inp.leftHanded,
    wireX,
    nutX,
    openX: P.tuning + P.open / 2,
    tuningX: P.tuning / 2 + 1,
    boardTop,
    boardBottom,
    stringY,
    spacing,
    fretNumberY: boardBottom + 12,
    headerY: 12,
  };
}

/** Screen x of a right-handed x. */
export function screenX(g: FretboardGeometry, x: number): number {
  return g.leftHanded ? g.width - x : x;
}

/** Right-handed x of a screen x. */
export function boardX(g: FretboardGeometry, x: number): number {
  return g.leftHanded ? g.width - x : x;
}

/** Right-handed centre x of a physical fret (0 = open, at the nut). */
export function fretCenterX(g: FretboardGeometry, physFret: number): number {
  if (physFret <= 0) return g.openX;
  const f = Math.min(physFret, g.frets);
  return (g.wireX[f - 1] + g.wireX[f]) / 2;
}

/** Width of a physical fret space (the open zone for 0). */
export function fretWidth(g: FretboardGeometry, physFret: number): number {
  if (physFret <= 0) return g.nutX - g.tuningX;
  const f = Math.min(physFret, g.frets);
  return g.wireX[f] - g.wireX[f - 1];
}

/**
 * Where a note sits: right-handed x/y for a string and a fret relative to the
 * capo. With a capo, open strings sit on the capo's fret space.
 */
export function notePosition(g: FretboardGeometry, string: number, fret: number, capo = 0): { x: number; y: number } | null {
  if (string < 1 || string > g.strings || fret < 0) return null;
  const phys = fret + capo;
  if (phys > g.frets) return null;
  return { x: fretCenterX(g, phys), y: g.stringY[string - 1] };
}

/** Dot radius for a fret space. */
export function dotRadius(g: FretboardGeometry, physFret: number): number {
  const w = physFret <= 0 ? FRETBOARD_PAD.open : fretWidth(g, physFret);
  return Math.max(4, Math.min(11, g.spacing * 0.44, w * 0.42));
}

export interface FretHit {
  string: number;
  /** Relative to the capo (0 = open, also when tapping behind the capo). */
  fret: number;
}

/** String/fret under a screen point, or null outside the board. */
export function hitTestFretboard(g: FretboardGeometry, x: number, y: number, capo = 0): FretHit | null {
  const bx = boardX(g, x);
  if (y < g.boardTop - 2 || y > g.boardBottom + 2) return null;
  const s = Math.floor((y - g.boardTop) / g.spacing) + 1;
  const string = Math.max(1, Math.min(g.strings, s));
  if (bx < FRETBOARD_PAD.tuning || bx > g.wireX[g.frets] + 2) return null;
  let phys = 0;
  if (bx > g.nutX) {
    // First wire at or right of bx.
    let lo = 1;
    let hi = g.frets;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (g.wireX[mid] < bx) lo = mid + 1;
      else hi = mid;
    }
    phys = lo;
  }
  return { string, fret: Math.max(0, phys - capo) };
}
